import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthProvider";
import { streamChat } from "../api/assistant";
import type { ChatMessage, PageContext, StreamChunk, ToolCallInfo } from "../domain/types";
import type { MapCommand } from "../../map-bridge/MapBridgeContext";
import { useMapBridgeOptional } from "../../map-bridge/MapBridgeContext";

let msgId = 0;
function nextId() {
  return `msg-${++msgId}-${Date.now()}`;
}

/** Convert an SSE map_action chunk to a MapCommand for the bridge. */
function mapActionToCommand(chunk: StreamChunk): MapCommand | null {
  switch (chunk.action) {
    case "flyTo":
      if (chunk.lat != null && chunk.lng != null)
        return { type: "flyTo", lat: chunk.lat, lng: chunk.lng, zoom: chunk.zoom };
      break;
    case "highlightBA":
      if (chunk.ba_code)
        return { type: "highlightBA", baCode: chunk.ba_code };
      break;
    case "highlightGenerators":
      if (chunk.generators?.length)
        return { type: "highlightGenerators", generators: chunk.generators };
      break;
    case "setFilters":
      if (chunk.filters)
        return { type: "setFilters", filters: chunk.filters };
      break;
    case "addMarker":
      if (chunk.lat != null && chunk.lng != null)
        return { type: "addMarker", lat: chunk.lat, lng: chunk.lng, label: chunk.label, color: chunk.color };
      break;
    case "clearHighlights":
      return { type: "clearHighlights" };
    case "navigateTo":
      if (chunk.path)
        return { type: "navigateTo", path: chunk.path };
      break;
  }
  return null;
}

export function useChat() {
  const { token } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const bridge = useMapBridgeOptional();

  // Sync map bridge state → chat page context
  useEffect(() => {
    if (!bridge) return;
    const ms = bridge.mapState;
    if (!ms.page) return;
    setPageContext({
      page: ms.page,
      ba_code: ms.selectedBaCode ?? undefined,
      selected_generator: ms.selectedGenerator
        ? { plant_id: ms.selectedGenerator.plantId, generator_id: ms.selectedGenerator.generatorId }
        : undefined,
      active_filters: ms.activeFilters,
      view_mode: ms.viewMode,
      zoom_level: ms.zoom,
    });
  }, [bridge?.mapState]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = useCallback(
    async (content: string) => {
      if (!token || !content.trim() || isStreaming) return;

      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: content.trim(),
        timestamp: Date.now(),
      };

      const assistantMsg: ChatMessage = {
        id: nextId(),
        role: "assistant",
        content: "",
        timestamp: Date.now(),
        toolCalls: [],
      };

      const updatedMessages = [...messages, userMsg];
      setMessages([...updatedMessages, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const assistantId = assistantMsg.id;

      const onChunk = (chunk: StreamChunk) => {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === assistantId);
          if (idx === -1) return prev;

          const updated = [...prev];
          const msg = { ...updated[idx] };

          if (chunk.type === "text" && chunk.text) {
            msg.content += chunk.text;
          } else if (chunk.type === "tool_call" && chunk.name) {
            msg.toolCalls = [
              ...(msg.toolCalls || []),
              { name: chunk.name, status: "pending" },
            ];
          } else if (chunk.type === "tool_result" && chunk.name) {
            msg.toolCalls = (msg.toolCalls || []).map((tc) =>
              tc.name === chunk.name && tc.status === "pending"
                ? { ...tc, status: (chunk.status as ToolCallInfo["status"]) || "ok" }
                : tc
            );
          } else if (chunk.type === "map_action" && chunk.action && bridge) {
            // Convert SSE map_action to MapCommand and dispatch
            const cmd = mapActionToCommand(chunk);
            if (cmd) bridge.dispatchCommand(cmd);
            // Don't modify message content for map actions
          } else if (chunk.type === "error") {
            msg.content += chunk.error
              ? `\n\n*Error: ${chunk.error}*`
              : "\n\n*An error occurred*";
          }

          updated[idx] = msg;
          return updated;
        });
      };

      const onDone = () => {
        setIsStreaming(false);
        abortRef.current = null;
      };

      try {
        await streamChat(
          updatedMessages,
          token,
          pageContext as Record<string, unknown> | null,
          onChunk,
          onDone,
          controller.signal
        );
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [token, messages, isStreaming, pageContext, bridge]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
    abortRef.current = null;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isStreaming,
    isOpen,
    setIsOpen,
    pageContext,
    setPageContext,
    sendMessage,
    stopStreaming,
    clearMessages,
  };
}
